import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import { isAbsentFormDate, serializeApiDate } from './api-dates'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

/** Formati data usati in init defaults e payload legacy. */
const PARSE_FORMATS = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD',
]

/**
 * @param {string} value
 * @returns {import('dayjs').Dayjs | null}
 */
function parseStringToDayjs(value) {
  const trimmed = value.trim()
  if (!trimmed || trimmed === 'Invalid Date') return null

  let d = dayjs(trimmed)
  if (d.isValid()) return d

  for (const fmt of PARSE_FORMATS) {
    d = dayjs(trimmed, fmt, true)
    if (d.isValid()) return d
  }
  return null
}

/**
 * @param {unknown} value
 * @returns {import('dayjs').Dayjs | null}
 */
function toDayjs(value) {
  if (value == null || value === '') return null
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null
  if (value instanceof Date) {
    const d = dayjs(value)
    return Number.isNaN(d.valueOf()) ? null : d
  }
  if (typeof value === 'string') return parseStringToDayjs(value)
  if (typeof value === 'number') {
    const d = dayjs(value)
    return d.isValid() ? d : null
  }
  return null
}

/**
 * ISO UTC completo per GET /Crash/new_crash_id?crash_date= (prefisso ID da giorno UTC).
 * @param {unknown} value
 * @returns {string}
 */
export function formatCrashDateForNewIdQuery(value) {
  return serializeApiDate(toDayjs(value) ?? dayjs()) ?? dayjs().utc().toISOString()
}

/**
 * Serializza un valore data/ora in ISO UTC (`…Z`).
 * @param {unknown} value
 * @returns {unknown}
 */
export function serializeUtcIsoForApi(value) {
  if (isAbsentFormDate(value)) return null
  const serialized = serializeApiDate(toDayjs(value) ?? value)
  return serialized
}

/**
 * Mappa label campo → tipo (`date` | `datetime`) dalla configuration init.
 * @param {Record<string, { elements?: Array<{ label?: string, type?: string }> }>} [formModel]
 * @returns {Map<string, 'date' | 'datetime'>}
 */
export function buildDateFieldTypeMap(formModel) {
  /** @type {Map<string, 'date' | 'datetime'>} */
  const map = new Map()
  if (!formModel || typeof formModel !== 'object') return map

  for (const section of Object.values(formModel)) {
    if (!section?.elements || !Array.isArray(section.elements)) continue
    for (const elem of section.elements) {
      if (!elem?.label) continue
      if (elem.type === 'date' || elem.type === 'datetime') {
        map.set(elem.label, elem.type)
      }
    }
  }
  return map
}

/**
 * Campi solo-data: ISO UTC (allineato al backend).
 * @param {unknown} value
 * @returns {unknown}
 */
export function serializeDateOnlyForApi(value) {
  return serializeUtcIsoForApi(value)
}

/**
 * Campi data+ora: ISO UTC.
 * @param {unknown} value
 * @returns {unknown}
 */
export function serializeDateTimeForApi(value) {
  return serializeUtcIsoForApi(value)
}

/**
 * @param {unknown} value
 * @param {'date' | 'datetime' | undefined} fieldType
 * @returns {unknown}
 */
export function serializeFieldDateForApi(value, fieldType) {
  if (fieldType === 'date') return serializeDateOnlyForApi(value)
  if (fieldType === 'datetime') return serializeDateTimeForApi(value)
  return value
}

/**
 * Normalizza ricorsivamente date/datetime nel body crash (POST/PATCH /crash, sync, ZIP).
 * @param {unknown} payload
 * @param {Record<string, unknown>} [formModel]
 * @returns {unknown}
 */
export function normalizeCrashPayloadDates(payload, formModel) {
  const typeMap = buildDateFieldTypeMap(formModel)
  return normalizeNode(payload, typeMap)
}

export { parseApiDate, serializeApiDate } from './api-dates'

/**
 * @param {unknown} node
 * @param {Map<string, 'date' | 'datetime'>} typeMap
 * @returns {unknown}
 */
function normalizeNode(node, typeMap) {
  if (node == null) return node
  if (Array.isArray(node)) {
    return node.map((item) => normalizeNode(item, typeMap))
  }
  if (typeof node !== 'object') return node
  if (dayjs.isDayjs(node) || node instanceof Date) return node

  /** @type {Record<string, unknown>} */
  const out = {}
  for (const [key, value] of Object.entries(node)) {
    const fieldType = typeMap.get(key)
    if (fieldType) {
      out[key] = serializeFieldDateForApi(value, fieldType)
    } else if (value != null && typeof value === 'object') {
      out[key] = normalizeNode(value, typeMap)
    } else {
      out[key] = value
    }
  }
  return out
}
